/**
 * 每日版本滞后名单（N-176，2026-10-26 维护者拍板 D1=A / D2=A）的线上契约单源。
 *
 * 生产方：企业后端 `apps/web/server/enterprise/version-lag.ts` 每天算一份（窗口 = 过去 7 天，
 * 对象 = 组织在职花名册）：设备表（v0.4.11 起每台电脑上报）与员工已上传会话记录里的自报版本
 * 合并取较新，与本组织推荐最低版本比较。
 * 消费方：
 * - admin-portal `POST /api/orgs/:orgCode/version-lag-digests` → 飞书私信维护者本人（D2=A）；
 * - 企业后端服务面 `GET /api/enterprise/service/client-devices` 多带的 `versionLag` 字段 →
 *   管理后台成员列表「DOSIA 版本」列标「低于推荐最低版本」。
 *
 * 两个消费方都过 {@link parseVersionLagReport}：整份严格校验，任何一条坏 → 整份作废（不丢行——
 * 丢一行 = 悄悄漏掉一个落后的员工）。
 */
/** 客户端从这一版起在每个鉴权请求上报版本头（设备表只有这之后的电脑）。 */
export const DEVICE_REPORTING_SINCE_VERSION = "0.4.11";
/** 名单窗口：「这周用过 DOSIA」= 过去 7 天。 */
export const VERSION_LAG_WINDOW_DAYS = 7;
const VERSION_RE = /^\d{1,4}\.\d{1,4}\.\d{1,6}$/;
function isRecord(v) {
    return typeof v === "object" && v !== null && !Array.isArray(v);
}
function isIso(v) {
    return typeof v === "string" && v.length > 0 && !Number.isNaN(Date.parse(v));
}
function isVersionOrNull(v) {
    return v === null || (typeof v === "string" && VERSION_RE.test(v));
}
function isBasis(v) {
    return v === "device" || v === "session" || v === "no-device-report";
}
function isUndeterminedReason(v) {
    return v === "pi-at-least-reporting" || v === "no-evidence";
}
function parseEntry(raw) {
    if (!isRecord(raw))
        return null;
    const { userId, version, basis, deviceVersion, sessionVersion, lastActiveAt, lastDeviceReport } = raw;
    if (typeof userId !== "string" || !userId)
        return null;
    if (!isBasis(basis) || !isVersionOrNull(version) || !isVersionOrNull(deviceVersion) || !isVersionOrNull(sessionVersion)) {
        return null;
    }
    if ((basis === "no-device-report") !== (version === null))
        return null;
    if (!isIso(lastActiveAt))
        return null;
    let report = null;
    if (lastDeviceReport !== null) {
        if (!isRecord(lastDeviceReport))
            return null;
        const { version: v, at } = lastDeviceReport;
        if (typeof v !== "string" || !VERSION_RE.test(v) || !isIso(at))
            return null;
        report = { version: v, at };
    }
    return { userId, version, basis, deviceVersion, sessionVersion, lastActiveAt, lastDeviceReport: report };
}
function parseUndetermined(raw) {
    if (!isRecord(raw))
        return null;
    const { userId, reason, lastActiveAt } = raw;
    if (typeof userId !== "string" || !userId || !isUndeterminedReason(reason) || !isIso(lastActiveAt))
        return null;
    return { userId, reason, lastActiveAt };
}
/** 严格反序列化 guard：任何字段 / 任何一条不合形状 → null。 */
export function parseVersionLagReport(raw) {
    if (!isRecord(raw))
        return null;
    const { computedAt, windowStart, minRecommendedVersion, activeUserCount, entries, undetermined } = raw;
    if (!isIso(computedAt) || !isIso(windowStart))
        return null;
    if (typeof minRecommendedVersion !== "string" || !VERSION_RE.test(minRecommendedVersion))
        return null;
    if (typeof activeUserCount !== "number" || !Number.isInteger(activeUserCount) || activeUserCount < 0)
        return null;
    if (!Array.isArray(entries) || !Array.isArray(undetermined))
        return null;
    const parsedEntries = [];
    for (const e of entries) {
        const p = parseEntry(e);
        if (!p)
            return null;
        parsedEntries.push(p);
    }
    const parsedUndetermined = [];
    for (const u of undetermined) {
        const p = parseUndetermined(u);
        if (!p)
            return null;
        parsedUndetermined.push(p);
    }
    return {
        computedAt,
        windowStart,
        minRecommendedVersion,
        activeUserCount,
        entries: parsedEntries,
        undetermined: parsedUndetermined,
    };
}
//# sourceMappingURL=enterprise-version-lag.js.map